import { getSupabaseClient } from "@veorest/api-client";
import type { TranslationKey } from "../i18n";
import type { PartnerDriverMode } from "./partnerDrivers";

export type PartnerDispatchMode = "manual" | "bypass";

export interface PartnerProfile {
  id: string;
  name: string;
  slug: string;
  status: string;
  contact_email: string | null;
  logo_url: string | null;
  dispatch_mode: PartnerDispatchMode;
  oelox_fallback_enabled: boolean;
  /** OWN = partner keeps full delivery fee; PAID = tracked via driver wallet. */
  driver_mode_default: PartnerDriverMode;
  /** Per completed trip OELOX charges this partner — Partner billing config (IQD). */
  oelox_per_trip_fee_iqd: number;
  /** 170: default per-trip driver pay when a driver has no override (IQD). */
  driver_trip_fee_default: number;
  user_id: string | null;
  display_name: string | null;
  role: string | null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function str(v: unknown, fallback = ""): string {
  if (v == null) return fallback;
  return String(v);
}

function mapProfile(raw: Record<string, unknown>): PartnerProfile {
  const mode = str(raw.dispatch_mode, "manual");
  return {
    id: str(raw.id),
    name: str(raw.name),
    slug: str(raw.slug),
    status: str(raw.status),
    contact_email: raw.contact_email != null ? str(raw.contact_email) : null,
    logo_url: raw.logo_url != null ? str(raw.logo_url) : null,
    dispatch_mode: mode === "bypass" ? "bypass" : "manual",
    oelox_fallback_enabled: raw.oelox_fallback_enabled === true,
    driver_mode_default: raw.driver_mode_default === "paid" ? "paid" : "own",
    oelox_per_trip_fee_iqd: Number(raw.oelox_per_trip_fee_iqd ?? raw.oelox_trip_fee_iqd ?? 0) || 0,
    driver_trip_fee_default: Number(raw.driver_trip_fee_default ?? 0) || 0,
    user_id: raw.user_id != null ? str(raw.user_id) : null,
    display_name: raw.display_name != null ? str(raw.display_name) : null,
    role: raw.role != null ? str(raw.role) : null,
  };
}

export async function getPartnerProfile(partnerId: string): Promise<PartnerProfile> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_get_profile", {
    p_partner_id: partnerId,
  });
  if (error) throw error;
  const row = asRecord(data);
  if (!row?.id) throw new Error("partner_profile_missing");
  return mapProfile(row);
}

/** Existing RPC only accepts logo_url in p_patch. */
export async function updatePartnerLogo(
  partnerId: string,
  logoUrl: string | null
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("partner_update_profile", {
    p_partner_id: partnerId,
    p_patch: { logo_url: logoUrl },
  });
  if (error) throw error;
}

export async function uploadPartnerLogo(
  partnerId: string,
  file: File
): Promise<string> {
  const supabase = getSupabaseClient();
  const ext = (file.name.split(".").pop() || "jpg").toLowerCase();
  const path = `${partnerId}/logo-${Date.now()}.${ext}`;
  const { error } = await supabase.storage.from("partner-images").upload(path, file, {
    contentType: file.type || "image/jpeg",
    upsert: true,
  });
  if (error) throw error;
  const { data } = supabase.storage.from("partner-images").getPublicUrl(path);
  return data.publicUrl;
}

export async function updatePartnerLogisticsSettings(
  partnerId: string,
  patch: {
    dispatchMode: PartnerDispatchMode;
    oeloxFallbackEnabled: boolean;
    /** Deprecated per-driver legacy default. Omit to keep the stored value. */
    driverModeDefault?: PartnerDriverMode;
  }
): Promise<void> {
  const supabase = getSupabaseClient();
  // Legacy p_driver_compensation_mode is intentionally no longer written.
  // p_driver_mode_default is only sent when provided (NULL keeps stored value).
  const { error } = await supabase.rpc("partner_update_logistics_settings", {
    p_partner_id: partnerId,
    p_dispatch_mode: patch.dispatchMode,
    p_oelox_fallback_enabled: patch.oeloxFallbackEnabled,
    ...(patch.driverModeDefault
      ? { p_driver_mode_default: patch.driverModeDefault }
      : {}),
  });
  if (error) throw error;
}

/** 170: default per-trip driver pay for the whole fleet (ops+). */
export async function setPartnerDriverTripFeeDefault(
  partnerId: string,
  amount: number
): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.rpc("partner_set_driver_trip_fee_default", {
    p_partner_id: partnerId,
    p_amount: amount,
  });
  if (error) throw error;
}

// ── Partner ↔ OELOX wallet (read-only for the partner; ops via SA) ────────────

export interface PartnerOeloxWallet {
  balance: number;
  negative_limit: number;
  currency: string;
  trip_fee_iqd: number;
}

export async function fetchPartnerOeloxWallet(
  partnerId: string
): Promise<PartnerOeloxWallet> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("partner_oelox_wallet_get", {
    p_partner_id: partnerId,
  });
  if (error) throw error;
  const row = asRecord(data) ?? {};
  return {
    balance: Number(row.balance ?? 0) || 0,
    negative_limit: Number(row.negative_limit ?? 0) || 0,
    currency: str(row.currency, "IQD"),
    trip_fee_iqd: Number(row.trip_fee_iqd ?? 0) || 0,
  };
}

/** Map settings RPC failures to i18n keys — call `t(mapSettingsRpcError(err))` at the UI. */
export function mapSettingsRpcError(err: unknown): TranslationKey {
  const msg =
    err && typeof err === "object" && "message" in err
      ? String((err as { message: unknown }).message)
      : String(err ?? "");
  if (msg.includes("partner_insufficient_role")) {
    return "errors.roleCannotChange";
  }
  if (msg.includes("partner_access_denied") || msg.includes("not_partner")) {
    return "errors.partnerAccessDenied";
  }
  if (msg.includes("invalid_dispatch_mode")) {
    return "errors.invalidDispatchMode";
  }
  if (msg.includes("invalid_driver_mode")) {
    return "errors.driverModeInvalid";
  }
  if (msg.includes("invalid_trip_fee")) {
    return "errors.invalidTripFee";
  }
  return "errors.somethingWrong";
}

/** @deprecated Prefer `mapSettingsRpcError` + `t(...)`. */
export function settingsRpcErrorMessage(err: unknown): string {
  const key = mapSettingsRpcError(err);
  const fallbacks: Record<string, string> = {
    "errors.roleCannotChange": "Your role cannot change these settings.",
    "errors.partnerAccessDenied": "Partner access denied.",
    "errors.invalidDispatchMode": "Invalid dispatch mode.",
    "errors.somethingWrong": "Something went wrong.",
  };
  return fallbacks[key] ?? "Something went wrong.";
}

const PREFS_KEY = "partner_web_preferences_v1";

export interface PartnerUiPreferences {
  /** Display-only: preferred default stage strip focus. */
  defaultStageFocus: "pending" | "processing" | "ready" | "all";
  notifyNewOrders: boolean;
  notifyDriverUpdates: boolean;
  notifyFallbackAlerts: boolean;
}

const DEFAULT_PREFS: PartnerUiPreferences = {
  defaultStageFocus: "all",
  notifyNewOrders: true,
  notifyDriverUpdates: true,
  notifyFallbackAlerts: true,
};

export function loadPartnerUiPreferences(): PartnerUiPreferences {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const parsed = JSON.parse(raw) as Partial<PartnerUiPreferences>;
    return {
      defaultStageFocus:
        parsed.defaultStageFocus === "pending" ||
        parsed.defaultStageFocus === "processing" ||
        parsed.defaultStageFocus === "ready" ||
        parsed.defaultStageFocus === "all"
          ? parsed.defaultStageFocus
          : DEFAULT_PREFS.defaultStageFocus,
      notifyNewOrders: parsed.notifyNewOrders !== false,
      notifyDriverUpdates: parsed.notifyDriverUpdates !== false,
      notifyFallbackAlerts: parsed.notifyFallbackAlerts !== false,
    };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

export function savePartnerUiPreferences(prefs: PartnerUiPreferences): void {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}
